import { ChallengeLabStatus } from '@/src/types/JamChallengeDetails';
import { i18nKeys } from '@/src/utils/i18n.utils';
import { Icon, IconProps } from '@amzn/awsui-components-react';
import React from 'react';
import { useTranslation } from 'react-i18next';
import styles from './ChallengeLabStatusIcon.module.scss';

interface ChallengeLabStatusIconProps {
  status: ChallengeLabStatus;
}

interface LabStatusDisplay {
  /** Cloudscape built-in icon name. */
  name: IconProps.Name;
  /** Cloudscape icon color variant (validated against the installed Icon component). */
  variant: IconProps.Variant;
  /** i18n key for the state's short label (used as the accessible label). */
  labelKey: string;
  /** i18n key for the state's hover/focus description. */
  descriptionKey: string;
}

/**
 * Per-state display mapping (icon name + color variant).
 *
 * READY and IN_PROGRESS intentionally share an icon: a started challenge with no task progress is
 * already "in progress" for tracking purposes, so the sidebar doesn't distinguish them.
 */
const LAB_STATUS_DISPLAY: Record<ChallengeLabStatus, LabStatusDisplay> = {
  [ChallengeLabStatus.NOT_STARTED]: {
    name: 'status-not-started',
    variant: 'subtle',
    labelKey: i18nKeys.myJams.challenges.details.labStatus.notStarted.label,
    descriptionKey: i18nKeys.myJams.challenges.details.labStatus.notStarted.description,
  },
  [ChallengeLabStatus.PREPARING]: {
    name: 'status-pending',
    variant: 'warning',
    labelKey: i18nKeys.myJams.challenges.details.labStatus.preparing.label,
    descriptionKey: i18nKeys.myJams.challenges.details.labStatus.preparing.description,
  },
  [ChallengeLabStatus.READY]: {
    name: 'status-in-progress',
    variant: 'warning',
    labelKey: i18nKeys.myJams.challenges.details.labStatus.ready.label,
    descriptionKey: i18nKeys.myJams.challenges.details.labStatus.ready.description,
  },
  [ChallengeLabStatus.IN_PROGRESS]: {
    name: 'status-in-progress',
    variant: 'warning',
    labelKey: i18nKeys.myJams.challenges.details.labStatus.inProgress.label,
    descriptionKey: i18nKeys.myJams.challenges.details.labStatus.inProgress.description,
  },
  [ChallengeLabStatus.SOLVED]: {
    name: 'status-positive',
    variant: 'success',
    labelKey: i18nKeys.myJams.challenges.details.labStatus.solved.label,
    descriptionKey: i18nKeys.myJams.challenges.details.labStatus.solved.description,
  },
};

/**
 * Compact status icon for a challenge's lab status, shown at the right of each sidebar row.
 *
 * Uses a native `title` tooltip rather than a Cloudscape `Popover` because this Popover version
 * only supports click triggers, not hover.
 */
const ChallengeLabStatusIcon: React.FC<ChallengeLabStatusIconProps> = ({ status }) => {
  const { t } = useTranslation();
  const display = LAB_STATUS_DISPLAY[status];
  const label = t(display.labelKey);
  const description = t(display.descriptionKey);

  return (
    <span
      // `title` for a mouse-hover tooltip; `aria-label` for assistive tech. Non-interactive (no
      // tabIndex) since it performs no action.
      className={styles.root}
      title={`${label}: ${description}`}
      aria-label={`${label}: ${description}`}
      role="img"
      data-testid={`challenge-lab-status-${status}`}
    >
      <Icon name={display.name} variant={display.variant} />
    </span>
  );
};

export default ChallengeLabStatusIcon;
